import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';

// ═══════════════════════════════════════════════════════════════
// FATIGUE OVERVIEW
// ═══════════════════════════════════════════════════════════════
export function FatigueOverviewPanel() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      // Get drivers + their fatigue records
      const driversSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'driver'), where('status', '==', 'active'), limit(500)));
      const driversList = driversSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Load fatigue for each
      const fatigueData = await Promise.all(driversList.map(async (driver) => {
        try {
          const fatigueDoc = await getDocs(query(collection(db, 'driverFatigue'), where('__name__', '==', driver.id), limit(1)));
          const fatigue = fatigueDoc.empty ? null : fatigueDoc.docs[0].data();
          return { ...driver, fatigue };
        } catch {
          return { ...driver, fatigue: null };
        }
      }));

      setDrivers(fatigueData);
      setLoading(false);
    })();
  }, []);

  const filtered = drivers.filter((d) => {
    if (filter === 'all') return true;
    if (filter === 'lockedOut') return d.fatigue?.lockedOut;
    if (filter === 'highHours') return (d.fatigue?.dailyHours || 0) >= 8;
    return true;
  });

  return (
    <div>
      <h1 className="font-serif mt-0 mb-4">Fatigue Overview</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Monitor driver hours and fatigue compliance under NSW limits (10hrs/day, 50hrs/week, 6 consecutive days max).
      </p>

      <div className="flex gap-2 mb-4">
        {[{ k: 'all', l: 'All' }, { k: 'highHours', l: 'High Hours (8+)' }, { k: 'lockedOut', l: 'Locked Out' }].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
            className={`px-3.5 py-1.5 rounded-full border-[1.5px] cursor-pointer text-[0.8rem] font-semibold ${
              filter === f.k ? 'border-brand-primary' : 'border-slate-200 bg-white'
            }`}
            style={filter === f.k ? { background: 'rgba(45,212,168,0.06)' } : undefined}>{f.l}</button>
        ))}
      </div>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-auto">
          <table className="w-full border-collapse text-[0.85rem]">
            <thead>
              <tr className="bg-slate-50">
                {['Driver', 'Today', 'Week', 'Consec. Days', 'Status'].map((h) => (
                  <th key={h} className="px-3.5 py-2.5 text-left font-semibold text-body border-b border-slate-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const f = d.fatigue || {};
                const dailyPct = (f.dailyHours || 0) / 10;
                const weeklyPct = (f.weeklyHours || 0) / 50;
                return (
                  <tr key={d.id} className="border-b border-slate-100">
                    <td className="px-3.5 py-2.5">
                      <div className="font-semibold">{d.firstName} {d.lastName?.[0]}.</div>
                      <div className="text-[0.75rem] text-muted">{d.referenceNumber || '—'}</div>
                    </td>
                    <td className="px-3.5 py-2.5">
                      <span className="font-semibold" style={{ color: dailyPct >= 0.9 ? '#dc2626' : dailyPct >= 0.7 ? '#f59e0b' : '#0a1628' }}>
                        {(f.dailyHours || 0).toFixed(1)}/10 hr
                      </span>
                    </td>
                    <td className="px-3.5 py-2.5">
                      <span className="font-semibold" style={{ color: weeklyPct >= 0.9 ? '#dc2626' : weeklyPct >= 0.7 ? '#f59e0b' : '#0a1628' }}>
                        {(f.weeklyHours || 0).toFixed(1)}/50 hr
                      </span>
                    </td>
                    <td className="px-3.5 py-2.5">{f.consecutiveDays || 0}/6</td>
                    <td className="px-3.5 py-2.5">
                      {f.lockedOut
                        ? <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>LOCKED OUT</span>
                        : <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold text-emerald-600" style={{ background: 'rgba(45,212,168,0.1)' }}>OK</span>
                      }
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted">No drivers match this filter</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RATINGS OVERVIEW
// ═══════════════════════════════════════════════════════════════
export function RatingsOverviewPanel() {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ avg: 0, total: 0, byStar: {} });

  useEffect(() => {
    (async () => {
      const snap = await getDocs(query(collection(db, 'ratings'), orderBy('createdAt', 'desc'), limit(500)));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRatings(data);

      // Stats
      const total = data.length;
      const sum = data.reduce((s, r) => s + (r.rating || 0), 0);
      const byStar = {};
      [1, 2, 3, 4, 5].forEach((star) => {
        byStar[star] = data.filter((r) => Math.round(r.rating) === star).length;
      });
      setStats({ avg: total > 0 ? sum / total : 0, total, byStar });
      setLoading(false);
    })();
  }, []);

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Ratings Overview</h1>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <>
          <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <div className="p-5 rounded-xl bg-white border border-slate-200">
              <div className="text-[0.85rem] text-body">Average Rating</div>
              <div className="text-[1.8rem] font-extrabold" style={{ color: '#fbbf24' }}>★ {stats.avg.toFixed(2)}</div>
            </div>
            <div className="p-5 rounded-xl bg-white border border-slate-200">
              <div className="text-[0.85rem] text-body">Total Ratings</div>
              <div className="text-[1.8rem] font-extrabold">{stats.total}</div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
            <h3 className="mt-0 mb-3 text-base">Distribution</h3>
            {[5, 4, 3, 2, 1].map((star) => {
              const count = stats.byStar[star] || 0;
              const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-3 mb-2">
                  <span className="w-[30px] text-[0.85rem] font-semibold">{star}★</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: '#fbbf24' }} />
                  </div>
                  <span className="w-[60px] text-[0.78rem] text-body text-right">{count} ({pct.toFixed(0)}%)</span>
                </div>
              );
            })}
          </div>

          <h3 className="mt-0 mb-3">Recent Ratings</h3>
          <div className="grid gap-2">
            {ratings.slice(0, 30).map((r) => (
              <div key={r.id} className="p-3 rounded-[10px] bg-white border border-slate-200">
                <div className="flex justify-between mb-1">
                  <span className="text-base" style={{ color: '#fbbf24' }}>{'★'.repeat(Math.round(r.rating || 0))}{'☆'.repeat(5 - Math.round(r.rating || 0))}</span>
                  <span className="text-[0.78rem] text-muted">
                    {r.createdAt?.toDate ? new Date(r.createdAt.toDate()).toLocaleDateString() : ''}
                  </span>
                </div>
                {r.comment && <div className="text-[0.85rem] text-body">{r.comment}</div>}
                <div className="text-[0.75rem] text-muted mt-1">
                  {r.raterRole === 'rider' ? 'Rider → Driver' : 'Driver → Rider'} · Trip {r.tripId?.substring(0, 8)}…
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CORPORATE ACCOUNTS
// ═══════════════════════════════════════════════════════════════
export function CorporateAccountsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'corporateAccounts'), orderBy('createdAt', 'desc'), limit(100)))
      .then((snap) => { setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
  }, []);

  return (
    <div>
      <h1 className="font-serif mt-0 mb-4">Corporate Accounts</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Business accounts with consolidated billing for multiple riders. Currently read-only — full CRUD coming in Phase 6.
      </p>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-3">
          {accounts.map((a) => (
            <div key={a.id} className="p-4 rounded-xl bg-white border border-slate-200">
              <div className="flex justify-between">
                <div>
                  <div className="font-bold">{a.companyName || a.name}</div>
                  <div className="text-[0.85rem] text-body">{a.contactName} · {a.contactEmail}</div>
                  <div className="text-[0.78rem] text-muted mt-1">
                    {(a.userIds || []).length} rider{(a.userIds || []).length !== 1 ? 's' : ''} ·
                    Limit ${a.monthlyLimit?.toFixed(2) || '—'} · Spent ${a.monthlySpend?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold"
                  style={{
                    background: a.status === 'active' ? 'rgba(45,212,168,0.1)' : 'rgba(148,163,184,0.15)',
                    color: a.status === 'active' ? '#059669' : '#5a6578',
                  }}>{a.status || 'inactive'}</span>
              </div>
            </div>
          ))}
          {accounts.length === 0 && <div className="p-8 text-center text-muted">No corporate accounts yet</div>}
        </div>
      )}
    </div>
  );
}